import { useCallback, useEffect, useMemo, useState } from 'react';
import { backendCloudReportApi } from './cloudReportApi';
import {
  buildReportPatch,
  changeCode,
  changeSectionText,
  revertSectionToTemplate,
  toEditableReport,
} from './cloudReportModel';

/**
 * The one copy of a cloud eval's pre-generated report
 * (cloud_eval_reports/{evaluationId}) that a page edits. The scoring form and
 * the Report tab both read and write this state, so a code changed in one
 * shows in the other straight away, saved or not. Loads and saves through
 * vasi-backend only; a save PATCHes just the changed fields, guarded by the
 * editedAt it loaded (409 when someone saved in between).
 */
export default function useCloudReport(evaluationId, api = backendCloudReportApi) {
  const [loaded, setLoaded] = useState({ status: 'loading', report: null });
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let active = true;
    setLoaded({ status: 'loading', report: null });
    setOriginal(null);
    setDraft(null);
    setMessage(null);
    api.loadReport(evaluationId)
      .then((report) => {
        if (!active) return;
        if (!report) {
          setLoaded({ status: 'missing', report: null });
          return;
        }
        const editable = toEditableReport(report);
        setLoaded({ status: 'ready', report });
        setOriginal(editable);
        setDraft(editable);
      })
      .catch((error) => {
        console.error('Failed to load the cloud eval report:', error);
        if (active) setLoaded({ status: 'error', report: null });
      });
    return () => { active = false; };
  }, [api, evaluationId]);

  // GET returns the published template's keys with the report; null when
  // there is no published template, so code changes keep the text as is.
  const templateKeys = loaded.report?.publishedTemplate?.keys ?? null;

  const changes = useMemo(
    () => (original && draft ? buildReportPatch(original, draft) : {}),
    [original, draft]
  );
  const dirty = Object.keys(changes).length > 0;

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const update = useCallback((change) => {
    setDraft((current) => (current ? change(current) : current));
  }, []);

  const setCode = useCallback((sectionId, codeName, value) => {
    update((current) => changeCode(current, sectionId, codeName, value, templateKeys));
  }, [update, templateKeys]);

  const setSectionText = useCallback((sectionId, text) => {
    update((current) => changeSectionText(current, sectionId, text));
  }, [update]);

  const revertSection = useCallback((sectionId) => {
    update((current) => revertSectionToTemplate(current, sectionId, templateKeys));
  }, [update, templateKeys]);

  const setSummaryText = useCallback((text) => {
    update((current) => ({ ...current, summary: { ...current.summary, text } }));
  }, [update]);

  async function save() {
    if (!dirty || saving) return;
    const savedDraft = draft;
    setSaving(true);
    setMessage(null);
    try {
      const saved = await api.saveReport({
        evaluationId,
        changes,
        baseEditedAt: loaded.report.editedAt ?? null,
      });
      setOriginal(savedDraft);
      setLoaded((current) => ({
        ...current,
        report: saved && typeof saved === 'object'
          ? { ...current.report, ...saved, publishedTemplate: saved.publishedTemplate ?? current.report.publishedTemplate }
          : { ...current.report, status: 'edited' },
      }));
      setMessage({ tone: 'ok', text: 'Report saved.' });
    } catch (error) {
      console.error('Failed to save the report:', error);
      setMessage({
        tone: 'error',
        text: error.status === 409
          ? 'Someone else saved this report after you opened it. Copy any wording you need, then reload the page to see their version.'
          : error.message || 'Could not save the report.',
      });
    } finally {
      setSaving(false);
    }
  }

  return {
    status: loaded.status,
    report: loaded.report,
    draft,
    templateKeys,
    changes,
    dirty,
    saving,
    message,
    setMessage,
    update,
    setCode,
    setSectionText,
    revertSection,
    setSummaryText,
    save,
  };
}
